import AsyncStorage from '@react-native-async-storage/async-storage';
import { OFFLINE_COMPANY_SWITCH_TTL_MS } from '@utils/sessionConstants';

const STORAGE_KEY = 'recentOnlineContextAccess';

type RecentOnlineContextAccessMap = Record<string, number>;

async function readMap(): Promise<RecentOnlineContextAccessMap> {
	try {
		const raw = await AsyncStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch {
		return {};
	}
}

async function writeMap(map: RecentOnlineContextAccessMap): Promise<void> {
	const cutoff = Date.now() - OFFLINE_COMPANY_SWITCH_TTL_MS * 2;
	const pruned = Object.fromEntries(
		Object.entries(map).filter(([, ts]) => typeof ts === 'number' && ts >= cutoff)
	);
	await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
}

/** Mark that the user validated this scope on the server (online switch or session). */
export async function recordOnlineContextAccess(
	sessionScopeId: string,
	accessedAt = Date.now()
): Promise<void> {
	if (!sessionScopeId) return;
	const map = await readMap();
	map[sessionScopeId] = accessedAt;
	await writeMap(map);
}

export async function hasRecentOnlineContextAccess(
	sessionScopeId: string,
	ttlMs = OFFLINE_COMPANY_SWITCH_TTL_MS
): Promise<boolean> {
	if (!sessionScopeId) return false;
	const map = await readMap();
	const accessedAt = map[sessionScopeId];
	if (typeof accessedAt !== 'number') return false;
	return Date.now() - accessedAt <= ttlMs;
}

/** Company and connected-user switches require recent online proof; personal account does not. */
export function offlineSwitchRequiresRecentOnlineProof(options: {
	companyNumber?: string | null;
	targetUserId?: string | null;
}): boolean {
	return !!(options.companyNumber || options.targetUserId);
}
